/**
 * Debounce a function call
 * Inspired by https://davidwalsh.name/javascript-debounce-function
 *
 * @param fn - The function to debounce
 * @param wait - The number of milliseconds to wait before invoking the function
 * @param immediate - Whether to invoke the function on the leading edge instead of the trailing edge
 * @returns The debounced function
 */
export function debounce<T extends (...args: never[]) => unknown>(
  fn: T,
  wait: number,
  immediate = false,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function caller(this: unknown, ...args: Parameters<T>): void {
    const later = (): void => {
      timeout = null;
      if (!immediate) {
        fn.apply(this, args);
      }
    };

    const callNow = immediate && !timeout;

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);

    if (callNow) {
      fn.apply(this, args);
    }
  };
}
